import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, MessageSquare, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function CatatanWaliPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [santriList, setSantriList] = useState([]);
  const [taList, setTaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ santri_id: "", tahun_ajaran: "", semester: "ganjil", catatan: "" });
  const [filterTA, setFilterTA] = useState("");
  const [saving, setSaving] = useState(false);

  const loadRefs = async () => {
    const [s, ta] = await Promise.all([
      api.get("/santri?limit=1000"),
      api.get("/tahun-ajaran"),
    ]);
    setSantriList(s.data.items || []);
    setTaList(ta.data.items || []);
    const active = (ta.data.items || []).find(t => t.is_active);
    if (active && !filterTA) { setFilterTA(active.tahun_ajaran); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterTA) q.append("tahun_ajaran", filterTA);
      const r = await api.get("/catatan-wali?" + q.toString());
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); }, [filterTA]);

  const openCreate = () => {
    const activeTA = taList.find(t => t.is_active);
    setEditing(null);
    setForm({ santri_id: "", tahun_ajaran: activeTA?.tahun_ajaran || "", semester: activeTA?.semester || "ganjil", catatan: "" });
    setDialogOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ santri_id: it.santri_id, tahun_ajaran: it.tahun_ajaran, semester: it.semester, catatan: it.catatan }); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.patch(`/catatan-wali/${editing.id}`, { catatan: form.catatan });
      else await api.post("/catatan-wali", form);
      toast.success("Catatan tersimpan");
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!confirm("Hapus catatan ini?")) return;
    try { await api.delete(`/catatan-wali/${it.id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const santriMap = Object.fromEntries(santriList.map(s => [s.id, s]));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><MessageSquare className="h-6 w-6 text-emerald-600" /> Catatan Wali Kelas</h1>
          <p className="text-sm text-slate-500">Catatan pribadi santri per tahun ajaran & semester.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterTA || "all"} onValueChange={(v) => setFilterTA(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Tahun Ajaran" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {taList.map(t => <SelectItem key={t.id} value={t.tahun_ajaran}>{t.tahun_ajaran}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-1" /> Tambah</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Belum ada catatan.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(it => {
            const s = santriMap[it.santri_id];
            return (
              <Card key={it.id}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{s?.nama || "?"}</p>
                      <p className="text-xs text-slate-500">NIS: {s?.nis || "-"} · {it.tahun_ajaran} · {it.semester}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{it.catatan}</p>
                  <p className="text-[10px] text-slate-400 mt-2">Oleh: {it.created_by_name || "-"}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Catatan" : "Tambah Catatan"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editing && (
              <div>
                <Label>Santri</Label>
                <Select value={form.santri_id} onValueChange={(v) => setForm(f => ({ ...f, santri_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih santri" /></SelectTrigger>
                  <SelectContent>{santriList.map(s => <SelectItem key={s.id} value={s.id}>{s.nama} ({s.nis})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {!editing && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tahun Ajaran</Label>
                  <Select value={form.tahun_ajaran} onValueChange={(v) => setForm(f => ({ ...f, tahun_ajaran: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{taList.map(t => <SelectItem key={t.id} value={t.tahun_ajaran}>{t.tahun_ajaran}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Semester</Label>
                  <Select value={form.semester} onValueChange={(v) => setForm(f => ({ ...f, semester: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ganjil">Ganjil</SelectItem>
                      <SelectItem value="genap">Genap</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div>
              <Label>Catatan</Label>
              <textarea className="w-full min-h-[120px] rounded-md border border-slate-200 p-2 text-sm"
                        value={form.catatan} onChange={(e) => setForm(f => ({ ...f, catatan: e.target.value }))} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
